import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Download, FileText, Image } from 'lucide-react';
import { DocumentsService } from '../services/documents-service';

interface FileRemovalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentId: string;
  file: {
    filename: string;
    file_type: string;
    file_size: number;
    uploaded_at: string;
    storage_path: string;
  };
  isLoading?: boolean;
}

export function FileRemovalDialog({
  isOpen,
  onClose,
  onConfirm,
  documentId,
  file,
  isLoading = false
}: FileRemovalDialogProps) {
  const [isDownloading, setIsDownloading] = React.useState(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    }
    return <FileText className="h-5 w-5 text-red-500" />;
  };

  const handleDownload = async () => {
    if (!DocumentsService.checkFilePermissions('download')) {
      return;
    }

    try {
      setIsDownloading(true);
      const { download_url } = await DocumentsService.getFileDownloadUrl(documentId);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Remove Document File?
          </DialogTitle>
          <DialogDescription>
            This will permanently remove the association between this document and the file.
            The file will be archived and can be restored manually if needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {getFileTypeIcon(file.file_type)}
                  <span className="font-medium">{file.filename}</span>
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>{formatFileSize(file.file_size)} • {file.file_type}</div>
                  <div>Uploaded {formatDate(file.uploaded_at)}</div>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {DocumentsService.checkFilePermissions('download') && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                {isDownloading ? 'Downloading...' : 'Download First'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading || !DocumentsService.checkFilePermissions('remove')}
          >
            {isLoading ? 'Removing...' : 'Remove File'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
